import { getRAGStatus } from 'utils/rag';

export interface EntityRow {
  label: string;
  branch_id: number | null;
  email?: string;
  score: number;
  quotes: number;
  quotes_per_week?: number;
  closing_ratio: number;
  nb_premium_total: number;
  p2p_percent: number;
  p2p_goal?: number;
  archived: number;
  leads_call_list: number;
  leads_organic: number;
  leads_direct: number;
}

export interface AggregateKPI {
  count: number;
  score: number;
  quotes: number;
  closing_ratio: number;
  nb_premium_total: number;
  avg_premium_total: number;
  archived: number;
  leads_total: number;
  rag_score: 'red' | 'amber' | 'green';
  rag_closing: 'red' | 'amber' | 'green';
  rag_quotes: 'red' | 'amber' | 'green';
}

/**
 * Computes a weighted aggregate KPI from a set of branch or broker rows.
 * - Score and Closing % are weighted by quote volume (not simple averages).
 * - NB Premium, Quotes, Archived, Leads are summed.
 * - Avg Premium = total NB / total quotes.
 * - P2P % is omitted — goal denominators are not available per-entity.
 */
export function computeAggregateKPI(rows: EntityRow[]): AggregateKPI {
  if (rows.length === 0) {
    return {
      count: 0,
      score: 0,
      quotes: 0,
      closing_ratio: 0,
      nb_premium_total: 0,
      avg_premium_total: 0,
      archived: 0,
      leads_total: 0,
      rag_score: 'red',
      rag_closing: 'red',
      rag_quotes: 'red',
    };
  }

  const totalQuotes = rows.reduce((s, r) => s + r.quotes, 0);
  const totalNB = rows.reduce((s, r) => s + r.nb_premium_total, 0);
  const totalArchived = rows.reduce((s, r) => s + r.archived, 0);
  const totalLeads = rows.reduce(
    (s, r) => s + r.leads_call_list + r.leads_organic + r.leads_direct,
    0,
  );

  const weightedScore =
    totalQuotes > 0
      ? rows.reduce((s, r) => s + r.score * r.quotes, 0) / totalQuotes
      : 0;

  const weightedClosing =
    totalQuotes > 0
      ? rows.reduce((s, r) => s + r.closing_ratio * r.quotes, 0) / totalQuotes
      : 0;

  const avgPremium = totalQuotes > 0 ? totalNB / totalQuotes : 0;

  // Approximate weekly quote rate for RAG (monthly total / 4.33 weeks)
  const quotesPerWeek = totalQuotes / 4.33;

  return {
    count: rows.length,
    score: Math.round(weightedScore),
    quotes: totalQuotes,
    closing_ratio: weightedClosing,
    nb_premium_total: totalNB,
    avg_premium_total: avgPremium,
    archived: totalArchived,
    leads_total: totalLeads,
    rag_score: getRAGStatus('score', Math.round(weightedScore)),
    rag_closing: getRAGStatus('closing_ratio', weightedClosing),
    rag_quotes: getRAGStatus('quotes_per_week', quotesPerWeek),
  };
}
